<template>
  <v-tooltip top nudge-bottom="166">
    <template v-slot:activator="{ on, attrs }">
      <div id="opdracht-card" style="width: 100%" v-bind="attrs" v-on="on">
        <v-card style="cursor: pointer">
          <v-card-text>
            <v-container>
              <v-row class="font-weight-bold" id="titel-row" style="flex-wrap: nowrap">
                <v-col> {{ $t('opdrachten.status') }}</v-col>
                <v-col class="text-align-center" v-html="$t('opdrachten.iltd')"></v-col>
                <v-col class="text-align-center"> {{ $t('opdrachten.label') }}</v-col>
              </v-row>
              <v-row style="height: 85px; flex-wrap: nowrap; overflow: hidden">
                <v-col>
                  {{ opdracht.status.omschrijving }}<br />
                  {{ formatDate(opdracht.statusDatum) }}
                </v-col>
                <v-col class="text-align-center">
                  <template v-if="!!opdracht.aandeelHernieuwbareEnergie.indicatorVoorLangetermijndoelstelling">
                    <v-progress-circular
                      id="ahe-graph"
                      :size="50"
                      :width="5"
                      :value="
                        opdracht.aandeelHernieuwbareEnergie.indicatorVoorLangetermijndoelstelling.waarde.toFixed(0)
                      "
                      :rotate="-90"
                      color="teal"
                    >
                      {{
                        opdracht.aandeelHernieuwbareEnergie.indicatorVoorLangetermijndoelstelling.waarde.toFixed(0) +
                        opdracht.aandeelHernieuwbareEnergie.indicatorVoorLangetermijndoelstelling.meeteenheid
                      }}
                    </v-progress-circular>
                  </template>
                  <template v-else-if="!!opdracht.aandeelHernieuwbareEnergie.energieLabel">
                    <span id="ahe-onbepaald">
                      {{ $t('aandeel-he.onbepaald') }}
                    </span>
                  </template>
                </v-col>
                <v-col class="text-align-center">
                  <icon-block-arrow
                    v-if="!!opdracht.aandeelHernieuwbareEnergie.energieLabel"
                    id="ahe-label"
                    color="teal"
                    :text="opdracht.aandeelHernieuwbareEnergie.energieLabel"
                    style="max-height: 75px; min-height: 50px; min-width: 30px; max-width: 75px"
                  ></icon-block-arrow>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-divider />
          <v-card-title style="background-color: rgba(0, 0, 0, 0.6)">
            <v-row style="width: 100%">
              <v-col
                style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap"
                class="white--text"
                id="opdracht-naam"
              >
                {{ opdracht.naam }}
              </v-col>
              <v-col cols="auto">
                <menu-button
                  :items="menuItems"
                  :button-attrs="{ icon: true, color: 'white' }"
                  id="gebouweenheid-menu-button"
                  @click-item="clickItem"
                />
              </v-col>
            </v-row>
          </v-card-title>
          <v-spacer />
          <v-card-text class="flex-column" style="height: 116px">
            <v-row v-for="(item, index) in items" :key="index" dense id="opdracht-info">
              <v-col v-if="item.label"> {{ item.label }}</v-col>
              <v-col v-if="item.value">{{ item.value }}</v-col>
            </v-row>
            <v-row v-if="downloadCertificaatCount > 0" dense>
              <v-col @click.stop="downloadCertificaat()" v-ripple>
                <a id="certificaat-download">
                  {{ $t('aandeel-he.download-pdf') }} ({{ downloadCertificaatCount }})
                  <v-icon color="primary">mdi-arrow-down</v-icon>
                </a>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <opdracht-delete-dialog
          v-model="showDeleteDialog"
          :opdracht="opdracht"
          @on-deleted="onDelete"
          @close="sluitDialoog"
        />
      </div>
    </template>
    {{ opdracht.naam }}
  </v-tooltip>
</template>

<script lang="ts">
import { Component, Inject, Prop, Provide, Vue } from 'vue-property-decorator';
import { OpdrachtClass } from '@/domain/interface/opdracht/OpdrachtClass';
import InfoCard from '@/components/card/InfoCard.vue';
import { MenuItemInterface } from '@/domain/interface/intern/MenuItemInterface';
import OpdrachtDeleteDialog from '@/components/dialog/OpdrachtDeleteDialog.vue';
import HernieuwbareEnergieScore from '@/views/partials/hernieuwbareEnergie/partials/HernieuwbareEnergieScore.vue';
import IconBlockArrow from '@/components/icon/custom/IconBlockArrow.vue';
import MenuButton from '@/components/menu/MenuButton.vue';
import DownloadService from '@/services/DownloadService';

@Component({
  components: {
    MenuButton,
    IconBlockArrow,
    HernieuwbareEnergieScore,
    InfoCard,
    OpdrachtDeleteDialog,
  },
})
export default class OpdrachtCard extends Vue {
  @Prop(Object) readonly opdracht!: OpdrachtClass;
  @Provide() showDeleteDialog = false;

  @Inject('downloadService')
  private downloadService: DownloadService;

  get items() {
    return [
      {
        label: this.$t('opdrachten.meetperiode'),
        value: this.$options.filters?.meetperiode(this.opdracht?.startMeetperiode, this.opdracht?.eindeMeetperiode),
      },
      {
        label: '',
        value: this.opdracht?.gebruikerErkenningsnummer + ' - ' + this.opdracht?.gebruikerVolledigeNaam(),
      },
    ];
  }

  get downloadCertificaatVisible(): string {
    return this.opdracht.links && this.opdracht.links.downloadZipCertificaten;
  }

  get downloadCertificaatCount(): number {
    return this.opdracht.aantalCertificaten;
  }

  downloadCertificaat() {
    if (this.opdracht.links && this.opdracht.links.downloadZipCertificaten) {
      this.downloadService.downloadFile(this.opdracht.links.downloadZipCertificaten, 'certificaten.zip');
    }
  }

  get menuItems(): MenuItemInterface[] {
    const menuItems = [];
    if (this.opdracht.links.wijzigen) {
      menuItems.push({
        label: this.$t('acties.aanpassen'),
        to: {
          name: 'opdrachtBewerken',
          params: {
            opdrachtUuid: this.opdracht?.id || 'undefined',
          },
        },
        action: this.$t('acties.aanpassen').toString(),
      });
    }

    if (this.opdracht?.links?.dupliceren) {
      menuItems.push({
        label: this.$t('acties.dupliceren'),
        to: {
          name: 'opdrachtDupliceren',
          params: {
            opdrachtUuid: this.opdracht?.id || 'undefined',
          },
        },
        action: this.$t('acties.dupliceren').toString(),
      });
    }
    if (this.opdracht?.links?.verwijderen) {
      menuItems.push({
        label: this.$t('acties.verwijderen'),
        to: {}, //not needed, is handled in OpdrachtDeleteDialog.vue
        action: this.$t('acties.verwijderen').toString(),
      });
    }

    return menuItems;
  }

  clickItem(item: MenuItemInterface) {
    if (item.action === this.$t('acties.verwijderen') && this.opdracht?.links?.verwijderen) {
      this.showDeleteDialog = true;
    } else {
      this.$emit('click-item', item);
    }
  }

  sluitDialoog() {
    this.showDeleteDialog = false;
  }

  onDelete() {
    this.sluitDialoog();
    this.$emit('delete-item');
  }

  formatDate(date: Date) {
    return this.$options.filters?.formatDate_ddmmyyyy_slash(date);
  }
}
</script>